import React, { useState } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import PropTypes from 'prop-types';
import PaidHitAvailability from './PaidHitAvailability';
import {
    countWords,
    truncateString,
    formatPlayerShots,
    getThirdPartyRanking,
    rankListTypeIds,
} from '../../../utils';
import styles from './aboutContent.scss';
import { playerGenderDescriptions, playerLevelDescriptions } from '../../../utils/members';

const Description = ({ data }) => {
    const [showCompleteDescription, setShowCompleteDescription] = useState(false);

    const wordLimit = 40;
    const getDisplayedText = () => {
        if (countWords(data.value) < wordLimit) {
            return data.value;
        }
        if (showCompleteDescription) {
            return data.value;
        }
        return truncateString(data.value, wordLimit);
    };

    const displayedText = getDisplayedText();

    const toggleDescription = () => {
        setShowCompleteDescription(!showCompleteDescription);
    };

    return (
        <div className={`${styles.value} ${styles.description} text-line-break`}>
            {displayedText}
            {countWords(data.value) >= wordLimit && (
                <span
                    className={`cursor-pointer text-teal ml8`}
                    onClick={toggleDescription}
                >
                    {showCompleteDescription ? 'Read less' : 'Read more'}
                </span>
            )}
        </div>
    );
};

Description.propTypes = {
    data: PropTypes.shape({
        value: PropTypes.string,
    }),
};

Description.defaultProps = {
    data: PropTypes.shape({
        value: '',
    }),
};

const AboutDataContent = ({ title, value }) => (
    <div className={`${styles.value}`}>
        {title}
        <span className={`${styles.bold}`}>
            {value}
        </span>
    </div>
);

AboutDataContent.propTypes = {
    title: PropTypes.string,
    value: PropTypes.string,
};

AboutDataContent.defaultProps = {
    title: '',
    value: '',
};

const setBioData = ({ props, userPreferences, coreUI }) => {
    const {
        profileData,
        displayCollegeRecruitingValues,
        isUserLoggedIn,
    } = props;

    const {
        description,
        heightInches,
        apparelBrand,
        atpOrWtaRank,
        backhand,
        birthPlace,
        college,
        collegeData,
        collegePosition,
        dominantHand,
        displayName,
        isPro,
        preferredBall,
        racketBrand,
        racketType,
        shoesBrand,
        shoesType,
        gender,
        thirdPartyRankings,
        favoritePlayers,
        favoritePlayersData,
        age,
        studentInfo,
        playerHighSchoolDetails,
        coachingExperienceStatus,
        coachingExperienceTypeId,
        playerExperienceStatus,
        playerExperienceTypeId,
        location,
        playerBio,
    } = profileData;

    const atpWtaRank = getThirdPartyRanking(thirdPartyRankings, gender === playerGenderDescriptions.FEMALE ? playerLevelDescriptions.WTA : playerLevelDescriptions.ATP, isPro);
    const { playerExperienceTypes, coachingExperienceTypes } = coreUI.types;

    const aboutData = [];
    const isMale = gender === playerGenderDescriptions.MALE;
    const { playerRankings, matchCount } = playerBio || {};
    const doesPlayerHaveVerifiedMatches = matchCount > 0;
    const { rankings } = playerRankings || {};
    const hasOnlyPickleballSet = !userPreferences?.showTennisContent && userPreferences?.showPickleballContent;
    const ratingTypeToDisplay = hasOnlyPickleballSet ? 'UTR-P' : 'UTR';
    const genericMessage = `${displayName} is a player located in ${location?.display}. Match results contributing to their ${ratingTypeToDisplay} rating and ranking will appear here.`;

    const getMatchHistoryData = () => {
        const threshold = 50000;
        const { FEMALE_PRO, FEMALE_WORLD, MALE_PRO, MALE_WORLD } = rankListTypeIds;
        const isFemale = gender === playerGenderDescriptions.FEMALE;
        const worldRankingType = isFemale ? FEMALE_WORLD : MALE_WORLD;
        const proRankingType = isFemale ? FEMALE_PRO : MALE_PRO;
        const worldRanking = rankings?.find(ranking => ranking?.rankListId === worldRankingType && ranking?.rank !== null)?.rank;
        const proRanking = rankings?.find(ranking => ranking?.rankListId === proRankingType && ranking?.rank !== null)?.rank;
        const isPlayerRankedTop50000 = (worldRanking <= threshold) || (proRanking <= threshold);
        const ranking = Math.min(worldRanking || threshold, proRanking || threshold);
        const rankingToDisplay = ranking?.toLocaleString();

        if (hasOnlyPickleballSet) {
            return genericMessage;
        }

        if (isPlayerRankedTop50000) {
            return `${displayName}, a tennis player located in ${location?.display}, is ranked ${rankingToDisplay} in the world among ${isMale ? 'male' : 'female'} players. This ranking is based on their UTR rating, which is calculated from their thirty most recent match results over the last twelve months.`;
        }
        return `${displayName} is a player located in ${location?.display}. Their UTR rating and ranking are calculated from their thirty most recent match results over the last twelve months.`;
    };

    const getBioData = () => {
        let bioData = '';
        if (doesPlayerHaveVerifiedMatches) {
            bioData = getMatchHistoryData();
        } else {
            bioData = genericMessage;
        }
        return bioData;
    };

    const userEnteredGraduationDate = studentInfo?.graduationDate;

    if (isUserLoggedIn) {
        if (displayCollegeRecruitingValues) {
            if (age) {
                aboutData.push({
                    title: 'Age',
                    value: `${age}`,
                });
            }
            if (userEnteredGraduationDate) {
                aboutData.push({
                    title: 'Graduating',
                    value: `${moment(userEnteredGraduationDate).year().toString()}`,
                });
            } else if (!userEnteredGraduationDate && playerHighSchoolDetails?.gradYear) {
                aboutData.push({
                    title: 'Graduating',
                    value: `${moment(playerHighSchoolDetails.gradYear).year().toString()}`,
                });
            }
        }
        if (description) {
            aboutData.push({
                title: 'Description',
                value: description,
            });
        }
        if (heightInches) {
            aboutData.push({
                title: 'Height',
                value: `${parseInt(heightInches / 12)}' ${heightInches % 12}"`,
            });
        }
        if (birthPlace) {
            aboutData.push({
                title: 'Birthplace',
                value: birthPlace,
            });
        }
        if (backhand || dominantHand) {
            aboutData.push({
                title: 'Plays',
                value: formatPlayerShots(dominantHand, backhand),
            });
        }
        if ((isPro || atpOrWtaRank) && atpWtaRank.length > 0) {
            aboutData.push({
                title: `${gender === 'Female' ? 'WTA' : 'ATP'} Rank`,
                value: atpWtaRank,
            });
        }

        if (college && collegeData) {
            let collegeValue = collegeData?.displayName;
            if (collegePosition) {
                collegeValue += ` #${collegePosition}`;
            }
            aboutData.push({
                title: 'College',
                value: collegeValue,
            });
        }

        if (playerExperienceTypeId && playerExperienceStatus) {
            aboutData.push({
                title: 'Playing Experience',
                value: `${playerExperienceStatus} ${playerExperienceTypes.find(type => type.id === playerExperienceTypeId)?.label}`,
            });
        }
        if (coachingExperienceTypeId && coachingExperienceStatus) {
            aboutData.push({
                title: 'Coaching Experience',
                value: `${coachingExperienceStatus} ${coachingExperienceTypes.find(type => type.id === coachingExperienceTypeId)?.label}`,
            });
        }
        if (racketBrand) {
            aboutData.push({
                title: 'Racket',
                value: racketType ? `${racketBrand} ${racketType}` : racketBrand,
            });
        }
        if (apparelBrand) {
            aboutData.push({
                title: 'Apparel',
                value: apparelBrand,
            });
        }
        if (preferredBall) {
            aboutData.push({
                title: 'Ball',
                value: preferredBall,
            });
        }
        if (shoesBrand) {
            aboutData.push({
                title: 'Shoes',
                value: shoesType ? `${shoesBrand} ${shoesType}` : shoesBrand,
            });
        }
        if (favoritePlayers && favoritePlayers.length > 0) {
            const favoritePlayerComponent = () => {
                return (
                    <>
                        {favoritePlayersData.map((s, i) => (
                            <React.Fragment key={s.id}>
                                <Link to={`/profiles/${s.id}`}>{s.displayName}</Link>
                                {i < favoritePlayersData.length - 1 ? ', ' : ''}
                            </React.Fragment>
                        ))}
                    </>
                );
            };
            aboutData.push({
                title: 'Favorite Players',
                value: favoritePlayerComponent(),
            });
        }
    }

    if (location?.display) {
    aboutData.push({
        title: 'Description',
        value: getBioData(),
    });
    }

    return aboutData;
};

const AboutContent = (props) => {
    const auth = useSelector(({ auth }) => auth);
    const coreUI = useSelector(({ coreUI }) => coreUI);
    const [showCompleteAbout, setShowCompleteAbout] = useState(false);
    const userPreferences = auth?.feedPreferences;

    const {
        profileData,
        className,
    } = props;

    const {
        paidHitAvailability,
    } = profileData;
    const aboutData = setBioData({ props, userPreferences, coreUI });
    const descriptionItemLimit = 4;
    const descriptionTitle = 'Description';

    return (
        <div>
            <div className="d-none d-md-block">
                {aboutData?.length > 0 && (
                    <>
                        <h6 className="text-semi mt48" data-testid="profile.about.title-desktop">About</h6>
                        <div className={`${styles.about} ${className || ''}`}>
                            {aboutData.map(data => {
                                if (data.title === descriptionTitle) {
                                    return (
                                        <Description
                                            data={data}
                                            key={data.title}
                                        />
                                    );
                                }
                                return (
                                    <AboutDataContent
                                        key={data.title}
                                        {...data}
                                    />
                                );
                            })}
                        </div>
                    </>
                )}

                <PaidHitAvailability
                    paidHitAvailability={paidHitAvailability}
                    className={className}
                />
            </div>
            <div className="d-md-none">
                {aboutData?.length > 0 && (
                    <>
                        <h6 className="text-semi mt48" data-testid="profile.about.title-mobile">About</h6>
                        <div className={`${styles.about} ${className || ''}`}>
                            {aboutData.map((data, i) => {
                                if (data.title === descriptionTitle) {
                                    return (
                                        <Description
                                            data={data}
                                            key={data.title}
                                        />
                                    );
                                }
                                if (i < descriptionItemLimit || showCompleteAbout) {
                                    return (
                                        <AboutDataContent
                                            key={data.title}
                                            {...data}
                                        />
                                    );
                                }
                                return null;
                            })}
                            {aboutData.length >= descriptionItemLimit && (
                                <div
                                    className={`${styles.value}`}
                                >
                                    <span
                                        className={`cursor-pointer text-teal`}
                                        onClick={() => {
                                            setShowCompleteAbout(!showCompleteAbout);
                                        }}
                                    >
                                        {showCompleteAbout ? 'Show less' : 'Show more'}
                                    </span>
                                </div>
                            )}
                        </div>
                    </>
                )}

                <PaidHitAvailability
                    paidHitAvailability={paidHitAvailability}
                    className={className}
                />
            </div>
        </div>
    );
};

AboutContent.propTypes = {
    profileData: PropTypes.shape({
        paidHitAvailability: PropTypes.arrayOf(PropTypes.shape({
            isAvailable: PropTypes.bool,
            day: PropTypes.string,
            availability: PropTypes.string,
        })),
    }).isRequired,
    className: PropTypes.string,
};

AboutContent.defaultProps = {
    className: '',
};

export default AboutContent;
